/**
 * ReviewHeader - Header bar for PRD Review interface.
 *
 * Shows:
 * - Back/close button
 * - Project context
 * - Progress tracking (answered/total questions)
 * - Auto-save status indicator
 * - Agent activity indicator
 */

import type { JSX } from 'react';
import { Button, Progress, Spinner } from '@heroui/react';
import { AlertCircle, ArrowLeft, Bot, Check } from 'lucide-react';

interface ReviewHeaderProps {
  isAgentTyping: boolean;
  isSaving: boolean;
  hasUnsavedChanges: boolean;
  questionCount?: number | null;
  answeredQuestionCount?: number;
  onClose: () => void;
}

export function ReviewHeader({
  answeredQuestionCount = 0,
  hasUnsavedChanges,
  isAgentTyping,
  isSaving,
  onClose,
  questionCount,
}: ReviewHeaderProps): JSX.Element {
  // Calculate progress percentage
  const hasQuestions = questionCount !== null && questionCount !== undefined && questionCount > 0;
  const progressPercentage = hasQuestions ? (answeredQuestionCount / questionCount) * 100 : 0;
  const isComplete = hasQuestions && answeredQuestionCount === questionCount;

  return (
    <header className="shrink-0 h-14 px-4 border-b border-border bg-surface-elevated flex items-center justify-between">
      {/* Left: Back button, title, and progress */}
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <Button
          isIconOnly
          variant="light"
          size="sm"
          onPress={onClose}
          aria-label="Close review and return to project"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-0.5">
            <h1 className="text-base font-semibold text-foreground">PRD Review</h1>
            {hasQuestions && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-foreground/70">
                  {answeredQuestionCount} of {questionCount} questions answered
                </span>
                <span
                  className={`text-xs font-semibold ${
                    isComplete
                      ? 'text-success'
                      : progressPercentage > 0
                        ? 'text-primary'
                        : 'text-muted'
                  }`}
                >
                  ({Math.round(progressPercentage)}%)
                </span>
              </div>
            )}
          </div>
          {hasQuestions && (
            <Progress
              size="sm"
              value={progressPercentage}
              color={isComplete ? 'success' : 'primary'}
              className="max-w-md"
              aria-label="Review progress"
            />
          )}
        </div>
      </div>

      {/* Right: Status indicators */}
      <div className="flex items-center gap-4">
        {/* Agent status */}
        {isAgentTyping && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10">
            <Bot className="w-4 h-4 text-primary" />
            <span className="text-xs font-medium text-primary">AI is working...</span>
          </div>
        )}

        {/* Save status */}
        <div className="flex items-center gap-2">
          {isSaving ? (
            <>
              <Spinner size="sm" color="default" />
              <span className="text-xs text-muted">Saving...</span>
            </>
          ) : hasUnsavedChanges ? (
            <>
              <AlertCircle className="w-4 h-4 text-warning" />
              <span className="text-xs text-warning">Unsaved changes</span>
            </>
          ) : (
            <>
              <Check className="w-4 h-4 text-success" />
              <span className="text-xs text-success">All changes saved</span>
            </>
          )}
        </div>

        {/* Close button (secondary) */}
        <Button size="sm" variant="flat" color="default" onPress={onClose}>
          Done
        </Button>
      </div>
    </header>
  );
}
